import { Avatar, IconLib, Pill, Select, SelectOption, SelectProps, Text, getKey } from '@fold-dev/core'
import React, { ReactElement, ReactNode, useContext, useEffect, useMemo } from 'react'
import { CommonContext } from './common.provider'
import { useAppStore } from '../../store/app'

const LAST_SELECTED_USER = 'LAST_SELECTED_USER'
window[LAST_SELECTED_USER] = null

export type UserSelectUser = {
    id: string | number
    name: string
    image?: string
}

export type UserSelectProps = {
    users: UserSelectUser[]
    onAdd?: (user: UserSelectUser) => void
    onDelete?: (user: UserSelectUser) => void
    variant?: 'default' | 'static'
    hideMe?: boolean
    footer?: ReactElement | null
    onDismiss?: () => void
    // passed via userSelectProps
    context?: any
} & any

export const UserSelect = (props: UserSelectProps) => {
    const { users = [], onAdd, onDelete, onDismiss, footer, variant = 'static', hideMe, context = {}, ...rest } = props
    const user = useAppStore((state) => state.user)
    const { availableUsers = [], onUserFilter } = useContext(CommonContext)
    const selected = useMemo(() => users.map(({ id }) => id), [users.length])
    const options: any = useMemo(() => {
        //const userAlreadyPresent = users.find((u) => u.id == user.id)
        const usersWithoutMe = availableUsers
            .filter((u: UserSelectUser) => user.id != u.id)
            .map((user: UserSelectUser) => ({
                key: user.id,
                label: user.name,
                prefix: (
                    <Avatar
                        key={user.image}
                        src={user.image}
                        name={user.name}
                        size="xs"
                    />
                ),
            }))

        return hideMe
            ? usersWithoutMe
            : [
                  {
                      key: user.id,
                      label: 'You',
                      prefix: (
                          <Avatar
                              key={user.image}
                              src={user.image}
                              name={user.name}
                              size="xs"
                          />
                      ),
                  },
                  ...usersWithoutMe,
              ]
    }, [availableUsers, users, user, hideMe])

    const handleUserSelect = (option: SelectOption) => {
        const availableUser = availableUsers.find((availableUser: any) => availableUser.id == option.key)
        const user = users.find((user: any) => user.id == option.key)

        if (!!user) {
            onDelete(user)
        } else {
            onAdd(availableUser)
            window[LAST_SELECTED_USER] = availableUser
        }
    }

    const handleInputKeyDown = (e) => {
        const { isBackspace, isEscape } = getKey(e)
        if (isEscape) onDismiss()
        if (isBackspace && !e.target.value) onDelete(users[users.length - 1])
    }

    const handleUserFilter = (value) => {
        onUserFilter(value, context)
    }

    useEffect(() => {
        handleUserFilter('')
    }, [])

    return (
        <Select
            {...rest}
            tagInput
            variant={variant}
            noListFocus
            trapFocus
            placeholder="User..."
            customPlaceholder="User..."
            selected={selected}
            options={options}
            filterDelay={500}
            selectListProps={{ noOptionsComponent: <Text p="var(--f-select-option-padding)">No users available</Text> }}
            tagInputFieldProps={{ onKeyDown: handleInputKeyDown }}
            onSelect={(option, dismiss, clear) => {
                clear()
                handleUserSelect(option)
            }}
            onFilter={handleUserFilter}
            footer={footer}
            render={() =>
                users.map((user: UserSelectUser, index: number) => (
                    <Pill
                        subtle
                        key={index}
                        size="sm"
                        prefix={
                            <Avatar
                                key={user.image}
                                src={user.image}
                                name={user.name}
                                size="xs"
                            />
                        }
                        suffix={
                            <IconLib
                                icon="x"
                                className="f-buttonize"
                                onClick={() => onDelete(user)}
                            />
                        }>
                        {user.name}
                    </Pill>
                ))
            }
        />
    )
}
